import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import {
  FiZap,
  FiLock,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiCopy,
  FiCheck,
  FiAlertTriangle,
  FiSave,
  FiRotateCcw,
  FiCheckCircle,
  FiExternalLink,
  FiInfo,
  FiShield,
  FiActivity,
  FiCreditCard,
  FiCheckSquare,
  FiHelpCircle,
  FiDatabase,
  FiServer,
  FiFileText,
  FiLayers,
  FiShoppingBag,
  FiBookmark,
  FiDollarSign,
  FiSearch,
  FiFilter,
  FiPlus,
  FiTrash2,
  FiInbox
} from "react-icons/fi";
import { Spin, message } from "antd";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";

const INTER = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };

export default function PaymentGatewaySettings({ onClose, zIndex = 101 }) {
  const permissions = useSelector(selectMenuPermissions);
  const formPermissions = permissions?.["paymentGatewaySettings"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  const rawBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8889";
  const rootApiUrl = rawBaseUrl.replace(/\/main\/?$/, "");
  const mainApiUrl = `${rootApiUrl}/main`;
  const commonApiUrl = `${rootApiUrl}/common`;

  const { dbCode: activeDb } = useDbContext();
  const dbCode = activeDb || "test";

  // Active Tab: "credentials" | "bank_mapping" | "webhook" | "transactions"
  const [activeTab, setActiveTab] = useState("credentials");

  // Form states
  const [isEnabled, setIsEnabled] = useState(true);
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [clearingLedgerId, setClearingLedgerId] = useState("");
  const [clearingLedgerName, setClearingLedgerName] = useState("");
  const [receiptFormId, setReceiptFormId] = useState("");
  const [receiptFormName, setReceiptFormName] = useState("");
  const [receiptVoucherType, setReceiptVoucherType] = useState("");
  const [autoPostReceipt, setAutoPostReceipt] = useState(true);

  // Dynamic Registered Forms from FormRegistration
  const [allRegisteredForms, setAllRegisteredForms] = useState([]);
  const [configuredForms, setConfiguredForms] = useState([]);
  const [selectedFormToAdd, setSelectedFormToAdd] = useState(null);
  const [selectedBankToAdd, setSelectedBankToAdd] = useState({ id: "", name: "" });
  const [selectedAutoPostToAdd, setSelectedAutoPostToAdd] = useState(true);
  const [formSearchQuery, setFormSearchQuery] = useState("");
  const [selectedFormForPreview, setSelectedFormForPreview] = useState(null);

  // Visibility toggles
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Status & UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: boolean, message: string, latencyMs?: number }
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [cabaLedgers, setCabaLedgers] = useState([]); // [{ label, value, ledcode, ledname }]
  const [schemeForms, setSchemeForms] = useState([]); // [{ label, value, formId, formName, voucherType, abbr }]
  const [lastConfigured, setLastConfigured] = useState(null);

  // Transactions Tab state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Determine key mode (Live vs Test)
  const isLiveMode = keyId.toLowerCase().startsWith("rzp_live");
  const isTestMode = keyId.toLowerCase().startsWith("rzp_test");

  // Construct Webhook URL
  const webhookUrl = `${rootApiUrl}/main/customerapp/payments/webhook/${dbCode}`;

  // Available forms from FormRegistration that are not yet added to configured list
  const availableFormsToAdd = allRegisteredForms.filter(
    (f) => !configuredForms.some((cf) => String(cf.formId) === String(f.formId))
  );

  const availableFormOptions = availableFormsToAdd.map((f) => ({
    label: `${f.formName} (${f.voucherType})${f.abbr ? ` [${f.abbr}]` : ""}`,
    value: f.formId,
    formId: f.formId,
    formName: f.formName,
    voucherType: f.voucherType,
    abbr: f.abbr
  }));

  // Filter configured forms list by search
  const filteredConfiguredForms = configuredForms.filter((row) => {
    const needle = formSearchQuery.toLowerCase().trim();
    if (!needle) return true;
    return (
      (row.formName && row.formName.toLowerCase().includes(needle)) ||
      (row.abbr && row.abbr.toLowerCase().includes(needle)) ||
      (row.voucherType && row.voucherType.toLowerCase().includes(needle)) ||
      String(row.formId).includes(needle)
    );
  });

  // Add chosen form to configured forms list
  const handleAddFormMapping = () => {
    if (!hasSavePermission && !hasEditPermission) {
      message.warning("You do not have permission to add voucher mappings.");
      return;
    }
    if (!selectedFormToAdd) {
      message.warning("Please select an official voucher form from the dropdown to add.");
      return;
    }

    const newMapping = {
      formId: selectedFormToAdd.formId,
      formName: selectedFormToAdd.formName,
      voucherType: selectedFormToAdd.voucherType,
      abbr: selectedFormToAdd.abbr || "",
      clearingLedgerId: selectedBankToAdd?.id ? parseInt(selectedBankToAdd.id, 10) : null,
      clearingLedgerName: selectedBankToAdd?.name || "",
      autoPostVoucher: selectedAutoPostToAdd,
      isEnabled: true
    };

    setConfiguredForms((prev) => [...prev, newMapping]);
    setSelectedFormForPreview(newMapping);
    setSelectedFormToAdd(null);
    setSelectedBankToAdd({ id: "", name: "" });
    setSelectedAutoPostToAdd(true);
    message.success(`Added "${newMapping.formName}" to online payment routes!`);
  };

  // Remove a form from configured forms list
  const handleRemoveFormMapping = (formId) => {
    if (!hasDeletePermission && !hasEditPermission) {
      message.warning("You do not have permission to delete voucher mappings.");
      return;
    }
    setConfiguredForms((prev) => {
      const updated = prev.filter((f) => f.formId !== formId);
      if (selectedFormForPreview?.formId === formId) {
        setSelectedFormForPreview(updated[0] || null);
      }
      return updated;
    });
    message.info("Form route removed. Click 'Save Form Mappings' to apply changes.");
  };

  // Toggle whether a configured form is active for online collection
  const handleToggleFormEnabled = (formId) => {
    setConfiguredForms((prev) =>
      prev.map((f) => {
        if (f.formId === formId) {
          const nextVal = !f.isEnabled;
          const updated = { ...f, isEnabled: nextVal };
          if (selectedFormForPreview?.formId === formId) {
            setSelectedFormForPreview(updated);
          }
          return updated;
        }
        return f;
      })
    );
  };

  // Assign specific settlement bank ledger to a configured form
  const handleUpdateFormBank = (formId, bankId, bankName) => {
    setConfiguredForms((prev) =>
      prev.map((f) => {
        if (f.formId === formId) {
          const updated = {
            ...f,
            clearingLedgerId: bankId ? parseInt(bankId, 10) : null,
            clearingLedgerName: bankName || ""
          };
          if (selectedFormForPreview?.formId === formId) {
            setSelectedFormForPreview(updated);
          }
          return updated;
        }
        return f;
      })
    );
  };

  // Toggle auto voucher generation on verified payment
  const handleToggleFormAutoPost = (formId) => {
    setConfiguredForms((prev) =>
      prev.map((f) => {
        if (f.formId === formId) {
          const updated = { ...f, autoPostVoucher: !f.autoPostVoucher };
          if (selectedFormForPreview?.formId === formId) {
            setSelectedFormForPreview(updated);
          }
          return updated;
        }
        return f;
      })
    );
  };

  // Fetch initial configuration & CABA ledgers & registered forms (exact table from FormRegistration.jsx)
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setTestResult(null);
    try {
      const settingsPromise = axios.get(`${mainApiUrl}/payment-gateway-settings/${dbCode}`);
      const ledgersPromise = axios.get(`${commonApiUrl}/selectCashAndBank/${dbCode}`);
      // Exact API used in FormRegistration.jsx to load registered forms
      const formsPromise = axios.get(`${mainApiUrl}/getformregdatas/${dbCode}`);

      const [settingsRes, ledgersRes, formsRes] = await Promise.all([settingsPromise, ledgersPromise, formsPromise]);

      let loadedId = "";
      let loadedName = "";
      let loadedFormId = "";
      let loadedFormName = "";
      let loadedVoucherType = "";
      let savedMappings = [];

      if (settingsRes.data && settingsRes.data.success && settingsRes.data.data) {
        const s = settingsRes.data.data;
        setKeyId(s.keyId || "");
        setKeySecret(s.keySecret || "");
        setWebhookSecret(s.webhookSecret || "");
        loadedId = s.clearingLedgerId ? String(s.clearingLedgerId) : "";
        loadedName = s.clearingLedgerName || "";
        loadedFormId = s.receiptFormId ? String(s.receiptFormId) : "";
        loadedFormName = s.receiptFormName || "";
        loadedVoucherType = s.receiptVoucherType || "";
        setClearingLedgerId(loadedId);
        setClearingLedgerName(loadedName);
        setReceiptFormId(loadedFormId);
        setReceiptFormName(loadedFormName);
        setReceiptVoucherType(loadedVoucherType);
        setIsEnabled(s.isEnabled !== false);
        setAutoPostReceipt(s.autoPostReceipt !== false);
        savedMappings = Array.isArray(s.voucherMappings) ? s.voucherMappings : [];
        setLastConfigured(s.updatedAt ? new Date(s.updatedAt).toLocaleString("en-IN") : null);
      }

      if (Array.isArray(ledgersRes.data)) {
        const formatted = ledgersRes.data.map((item) => ({
          label: item.LedName || item.ledname || "",
          value: item.ledcode,
          ledcode: item.ledcode,
          ledname: item.LedName || item.ledname || ""
        }));
        setCabaLedgers(formatted);

        // Auto-match name if ID exists but name is not saved in settings
        if (loadedId && !loadedName) {
          const matched = formatted.find((l) => String(l.value) === String(loadedId));
          if (matched) {
            setClearingLedgerName(matched.label);
          }
        }
      }

      // Load all registered forms directly from dbo.FormRegistration
      if (formsRes.data && Array.isArray(formsRes.data)) {
        const rawForms = formsRes.data.map((f) => ({
          formId: f.fmrEntryNo,
          formName: (f.fmrName || "").trim(),
          voucherType: (f.fmrTypeOfVoucher || "").trim(),
          abbr: (f.fmrAbbreviation || "").trim()
        }));
        setAllRegisteredForms(rawForms);

        let initialConfigured = [];
        if (savedMappings.length > 0) {
          initialConfigured = savedMappings.map((m) => {
            const fId = m.FormId || m.formId;
            const matched = rawForms.find((rf) => String(rf.formId) === String(fId));
            return {
              formId: fId,
              formName: (m.FormName || m.formName || matched?.formName || "").trim(),
              voucherType: (m.VoucherType || m.voucherType || matched?.voucherType || "RECEIPT").trim(),
              abbr: matched?.abbr || "",
              isEnabled: m.IsEnabled !== undefined ? (m.IsEnabled === 1 || m.IsEnabled === true) : Boolean(m.isEnabled),
              clearingLedgerId: m.ClearingLedgerId || m.clearingLedgerId || null,
              clearingLedgerName: m.ClearingLedgerName || m.clearingLedgerName || "",
              autoPostVoucher: m.AutoPostVoucher !== undefined ? (m.AutoPostVoucher === 1 || m.AutoPostVoucher === true) : Boolean(m.autoPostVoucher)
            };
          });
        }

        setConfiguredForms(initialConfigured);
        setSelectedFormForPreview(initialConfigured[0] || null);
      }
    } catch (err) {
      console.error("Error loading payment gateway settings / bank ledgers / forms:", err);
      message.error("Failed to load gateway settings, bank ledgers, or voucher forms.");
    } finally {
      setIsLoading(false);
    }
  }, [mainApiUrl, commonApiUrl, dbCode]);

  // Fetch recent online transactions
  const fetchTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const res = await axios.get(`${mainApiUrl}/payment-gateway-transactions/${dbCode}?limit=30`);
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setTransactions(res.data.data);
      }
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [mainApiUrl, dbCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    }
  }, [activeTab, fetchTransactions]);

  // Copy helper
  const handleCopy = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (type === "url") {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else if (type === "key") {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
    message.success("Copied to clipboard!");
  };

  // Test Connection to Razorpay API
  const handleTestConnection = async () => {
    if (!keyId.trim() || !keySecret.trim()) {
      message.warning("Please enter both Key ID and Key Secret to test connection.");
      return;
    }
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${mainApiUrl}/payment-gateway-settings/test-connection/${dbCode}`, {
        keyId: keyId.trim(),
        keySecret: keySecret.trim()
      });
      if (res.data && res.data.success) {
        setTestResult({
          success: true,
          message: res.data.data?.message || "Gateway Connection Verified Successfully",
          latencyMs: res.data.data?.latencyMs
        });
        message.success("Razorpay API connection verified successfully!");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to authenticate with Razorpay.";
      setTestResult({
        success: false,
        message: errorMsg
      });
      message.error(errorMsg);
    } finally {
      setIsTesting(false);
    }
  };

  // Save Settings
  const handleSave = async () => {
    if (!hasSavePermission && !hasEditPermission) {
      message.warning("You do not have permission to save or update Payment Gateway Settings.");
      return;
    }

    const primarySchemeForm = configuredForms.find(
      (f) => f.isEnabled && f.voucherType.toUpperCase().includes("SCHEME")
    ) || configuredForms[0];

    const recId = primarySchemeForm?.formId || (receiptFormId ? parseInt(receiptFormId, 10) : null);
    const recName = primarySchemeForm?.formName || receiptFormName || "";
    const recType = primarySchemeForm?.voucherType || receiptVoucherType || "";
    const parsedLedgerId = clearingLedgerId ? parseInt(clearingLedgerId, 10) : null;

    setIsSaving(true);
    try {
      const payload = {
        keyId: keyId.trim(),
        keySecret: keySecret.trim(),
        webhookSecret: webhookSecret.trim(),
        clearingLedgerId: parsedLedgerId,
        clearingLedgerName: clearingLedgerName ? clearingLedgerName.trim() : "",
        receiptFormId: recId,
        receiptFormName: recName.trim(),
        receiptVoucherType: recType.trim(),
        isEnabled,
        autoPostReceipt,
        voucherMappings: configuredForms
      };

      const res = await axios.post(`${mainApiUrl}/payment-gateway-settings/${dbCode}`, payload);
      if (res.data && res.data.success) {
        message.success("Payment Gateway settings & Voucher Mappings saved successfully!");
        setSavedSuccess(true);
        setTimeout(() => setSavedSuccess(false), 3000);
        setLastConfigured(new Date().toLocaleString("en-IN"));
        if (recId) setReceiptFormId(String(recId));
        if (recName) setReceiptFormName(recName);
        if (recType) setReceiptVoucherType(recType);
      }
    } catch (err) {
      console.error("Save error:", err);
      message.error(err.response?.data?.message || "Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      {/* ── Main Modal Container (Exact layout from AppConfigMaster) ── */}
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        
        {/* ── Window Header ───────────────────────────────────────────────────── */}
        <WindowHeader
          title="Online Payment Gateway Configuration"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── Top Toolbar ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200/80 px-4 py-2 shrink-0 flex flex-nowrap items-center justify-between gap-2.5 overflow-x-auto">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-11 h-11 bg-[#0d2347] rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
              <FiCreditCard className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-[17px] font-bold text-slate-800 leading-tight" style={INTER}>
                  Payment Gateway Settings
                </h1>
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 text-[10.5px] text-blue-900 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span>Company: <strong>{dbCode}</strong></span>
                </div>
              </div>
              <p className="text-[11.5px] text-slate-500 font-medium" style={INTER}>
                Razorpay merchant credentials, webhook synchronization &amp; bank settlement mapping
              </p>
            </div>
          </div>

          {/* Controls: Mode Badge + Active Toggle Switch + Refresh + Save */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0">
            {/* Mode Pill */}
            <span
              className={`border rounded-lg px-2.5 py-1 text-[11px] font-bold flex items-center gap-1.5 ${
                isLiveMode
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                  : isTestMode
                  ? "bg-amber-50 text-amber-800 border-amber-300"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
              style={INTER}
            >
              <FiLock className="text-[11px]" />
              <span>{isLiveMode ? "LIVE PRODUCTION" : isTestMode ? "TEST SANDBOX" : "MODE UNDEFINED"}</span>
            </span>

            {/* Status Stat Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                <FiCheckCircle className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider" style={INTER}>
                  Gateway Status
                </span>
                <span className={`text-xs font-extrabold leading-tight ${isEnabled ? "text-emerald-700" : "text-slate-500"}`} style={INTER}>
                  {isEnabled ? "ACTIVE" : "PAUSED"}
                </span>
              </div>
            </div>

            {/* Active Toggle Switch Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80">
              <span className="text-xs font-bold text-slate-700" style={INTER}>
                Accept App Payments
              </span>
              <button
                type="button"
                onClick={() => setIsEnabled(!isEnabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                  isEnabled ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    isEnabled ? "translate-x-4" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Refresh button */}
            <button
              type="button"
              onClick={activeTab === "transactions" ? fetchTransactions : fetchData}
              disabled={isLoading || loadingTransactions}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-xs cursor-pointer disabled:opacity-50"
              style={INTER}
              title="Refresh settings"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 text-slate-500 ${(isLoading || loadingTransactions) ? "animate-spin text-blue-600" : ""}`} />
              Refresh
            </button>

            {/* Save button */}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLoading || (!hasSavePermission && !hasEditPermission)}
              title={(!hasSavePermission && !hasEditPermission) ? "You do not have permission to save settings" : "Save settings"}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white shadow-xs transition bg-[#b78a39] hover:bg-[#a1782f] cursor-pointer disabled:opacity-50"
              style={INTER}
            >
              {savedSuccess ? (
                <>
                  <FiCheck className="w-3.5 h-3.5 text-white" />
                  Saved!
                </>
              ) : (
                <>
                  <FiSave className="w-3.5 h-3.5 text-white" />
                  {isSaving ? "Saving…" : "Save Settings"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Tab Switcher ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200 px-4 pt-0 shrink-0 flex items-end gap-0">
          {/* Credentials Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("credentials")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "credentials"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiLock className="w-3.5 h-3.5" />
            Gateway Credentials
          </button>

          {/* Bank Account Mapping Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("bank_mapping")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "bank_mapping"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiCreditCard className="w-3.5 h-3.5" />
            Bank &amp; Voucher Mapping
            {clearingLedgerName && (
              <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[9.5px] rounded-full font-bold leading-none">
                Bank Linked
              </span>
            )}
            {receiptFormId && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9.5px] rounded-full font-bold leading-none">
                Form #{receiptFormId}
              </span>
            )}
          </button>

          {/* Webhook & Security Diagnostics Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("webhook")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "webhook"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiShield className="w-3.5 h-3.5" />
            Webhook &amp; Security
          </button>

          {/* Online Payment Transactions Tab */}
          <button
            type="button"
            onClick={() => setActiveTab("transactions")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "transactions"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiActivity className="w-3.5 h-3.5" />
            Payment History &amp; Audit Logs
            {transactions.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[9.5px] rounded-full font-bold leading-none">
                {transactions.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Main Body (Clean 2-Panel Architecture) ───────────────────────────── */}
        <div className="flex-1 flex gap-4 p-4 min-h-0 bg-slate-50 overflow-hidden">
          
          {isLoading ? (
            <div className="w-full flex-1 flex flex-col items-center justify-center gap-3 p-12 bg-white rounded-xl border border-slate-200">
              <Spin size="large" />
              <span className="text-xs text-slate-500 font-medium" style={INTER}>
                Loading gateway configuration and accounts…
              </span>
            </div>
          ) : activeTab === "credentials" ? (
            /* ─────────────────────────────────────────────────────────────────── */
            /* ── TAB 1: GATEWAY CREDENTIALS & API SETUP ──────────────────────── */
            /* ─────────────────────────────────────────────────────────────────── */
            <>
              {/* ── LEFT: Credentials Form Panel (50% width) ── */}
              <div className="w-[50%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Form Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiLock className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        RAZORPAY MERCHANT CREDENTIALS
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Merchant credentials &amp; live connection status for your company
                      </p>
                    </div>
                    {/* Golden decorative accent */}
                    <div className="absolute right-0 top-0 h-full w-32 pointer-events-none rounded-tr-xl overflow-hidden">
                      <svg 
                        className="absolute right-0 top-0 h-full w-full pointer-events-none" 
                        viewBox="0 0 120 44" 
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <linearGradient id="goldGradCr" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#a67c1e" />
                            <stop offset="30%" stopColor="#f1c40f" />
                            <stop offset="50%" stopColor="#fbf3b9" />
                            <stop offset="70%" stopColor="#f1c40f" />
                            <stop offset="100%" stopColor="#aa7c11" />
                          </linearGradient>
                          <pattern id="honeycombCr" width="6" height="6" patternUnits="userSpaceOnUse">
                            <path d="M 3 0 L 6 1.5 L 6 4.5 L 3 6 L 0 4.5 L 0 1.5 Z" fill="none" stroke="#aa7c11" strokeWidth="0.4" opacity="0.35" />
                          </pattern>
                        </defs>
                        <polygon points="78,44 120,44 120,14" fill="url(#goldGradCr)" />
                        <polygon points="78,44 120,44 120,14" fill="url(#honeycombCr)" />
                        <line x1="78" y1="44" x2="120" y2="14" stroke="url(#goldGradCr)" strokeWidth="2.2" />
                        <line x1="66" y1="44" x2="108" y2="14" stroke="url(#goldGradCr)" strokeWidth="1.5" />
                      </svg>
                    </div>
                  </div>

                  {/* Form Scroll Area */}
                  <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1 min-h-0">
                    
                    {/* Gateway Provider Selection Card */}
                    <div className="flex flex-col gap-1.5 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5" style={INTER}>
                          <FiCreditCard className="text-blue-600" />
                          Payment Gateway Provider
                        </label>
                        <span className="text-[9.5px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Active &amp; Ready
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-0.5">
                        <div className="p-2 rounded-lg border-2 border-blue-600 bg-blue-50/50 flex flex-col gap-0.5 cursor-pointer shadow-2xs">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-blue-900" style={INTER}>Razorpay</span>
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          </div>
                          <span className="text-[9px] text-slate-500 font-medium">UPI, Cards, NetBanking</span>
                        </div>
                        <div className="p-2 rounded-lg border border-slate-200 bg-white/70 flex flex-col gap-0.5 opacity-60 cursor-not-allowed" title="PhonePe direct integration coming in future release">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-slate-700" style={INTER}>PhonePe</span>
                            <span className="text-[8.5px] font-bold text-amber-700 bg-amber-50 px-1 rounded">Soon</span>
                          </div>
                          <span className="text-[9px] text-slate-400 font-medium">Direct UPI Intent</span>
                        </div>
                        <div className="p-2 rounded-lg border border-slate-200 bg-white/70 flex flex-col gap-0.5 opacity-60 cursor-not-allowed" title="Paytm integration coming in future release">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-slate-700" style={INTER}>Paytm</span>
                            <span className="text-[8.5px] font-bold text-amber-700 bg-amber-50 px-1 rounded">Soon</span>
                          </div>
                          <span className="text-[9px] text-slate-400 font-medium">All-in-One Gateway</span>
                        </div>
                      </div>
                    </div>

                    {/* Key ID Field */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                          Key ID <span className="text-rose-500">*</span>
                        </label>
                        {keyId && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded ${isLiveMode ? "bg-emerald-100 text-emerald-800" : isTestMode ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                            {isLiveMode ? "Live Production Key" : isTestMode ? "Test Sandbox Key" : "Custom Key"}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={keyId}
                          onChange={(e) => setKeyId(e.target.value)}
                          placeholder="rzp_live_xxxxxxxx or rzp_test_xxxxxxxx"
                          className="w-full h-[32px] px-2.5 pr-8 border border-slate-200 rounded-md text-slate-800 font-mono text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                          style={{ border: "1px solid #cbd5e1", outline: "none", ...INTER }}
                        />
                        <button
                          type="button"
                          onClick={() => handleCopy(keyId, "key")}
                          className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                          title="Copy Key ID"
                        >
                          {copiedKey ? <FiCheck className="text-emerald-600 text-xs" /> : <FiCopy className="text-xs" />}
                        </button>
                      </div>
                      <span className="text-[9.5px] text-slate-400 font-medium">
                        Your public merchant identifier issued by Razorpay.
                      </span>
                    </div>

                    {/* Key Secret Field */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                          Key Secret <span className="text-rose-500">*</span>
                        </label>
                        <span className="text-[9.5px] text-slate-400">Stored securely &amp; never exposed to customer app</span>
                      </div>
                      <div className="relative">
                        <input
                          type={showKeySecret ? "text" : "password"}
                          value={keySecret}
                          onChange={(e) => setKeySecret(e.target.value)}
                          placeholder="Enter your Razorpay Key Secret"
                          className="w-full h-[32px] px-2.5 pr-8 border border-slate-200 rounded-md text-slate-800 font-mono text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                          style={{ border: "1px solid #cbd5e1", outline: "none", ...INTER }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowKeySecret(!showKeySecret)}
                          className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                          title={showKeySecret ? "Hide secret" : "Show secret"}
                        >
                          {showKeySecret ? <FiEyeOff className="text-xs" /> : <FiEye className="text-xs" />}
                        </button>
                      </div>
                    </div>

                    {/* Webhook Secret Field */}
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                          Webhook Secret
                        </label>
                        <span className="text-[9.5px] text-slate-400">Cryptographic signature verification</span>
                      </div>
                      <div className="relative">
                        <input
                          type={showWebhookSecret ? "text" : "password"}
                          value={webhookSecret}
                          onChange={(e) => setWebhookSecret(e.target.value)}
                          placeholder="Secret entered during webhook creation in Razorpay"
                          className="w-full h-[32px] px-2.5 pr-8 border border-slate-200 rounded-md text-slate-800 font-mono text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all bg-white font-medium"
                          style={{ border: "1px solid #cbd5e1", outline: "none", ...INTER }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                          {showWebhookSecret ? <FiEyeOff className="text-xs" /> : <FiEye className="text-xs" />}
                        </button>
                      </div>
                    </div>

                    {/* Live Gateway Test Connection Box */}
                    <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-3 flex flex-col gap-2 mt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5" style={INTER}>
                          <FiZap className="w-3.5 h-3.5 text-amber-600" />
                          Live Gateway Connection Check
                        </span>
                        <button
                          type="button"
                          onClick={handleTestConnection}
                          disabled={isTesting || !keyId || !keySecret}
                          className="px-3 py-1 bg-[#0f2d59] hover:bg-[#1a3d70] text-white text-[10.5px] font-bold rounded-md shadow-2xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                          style={INTER}
                        >
                          {isTesting ? (
                            <>
                              <FiRefreshCw className="w-3 h-3 animate-spin" />
                              Verifying…
                            </>
                          ) : (
                            <>
                              <FiActivity className="w-3 h-3 text-amber-300" />
                              Test Connection
                            </>
                          )}
                        </button>
                      </div>

                      {testResult && (
                        <div
                          className={`p-2 rounded-lg border text-[11px] flex items-center gap-2 font-medium ${
                            testResult.success
                              ? "bg-emerald-50/80 border-emerald-200 text-emerald-800"
                              : "bg-rose-50/80 border-rose-200 text-rose-800"
                          }`}
                          style={INTER}
                        >
                          {testResult.success ? (
                            <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <FiAlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                          )}
                          <div className="leading-tight">
                            <span>{testResult.message}</span>
                            {testResult.latencyMs && (
                              <span className="ml-1 text-[9.5px] opacity-80">({testResult.latencyMs} ms)</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Form Action Buttons at bottom of left panel */}
                  <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                    <button
                      type="button"
                      onClick={fetchData}
                      className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs"
                      style={INTER}
                    >
                      Reload Configuration
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving || isLoading}
                      className="h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs bg-[#b78a39] hover:bg-[#a67c32] text-white cursor-pointer disabled:opacity-50"
                      style={INTER}
                    >
                      {savedSuccess ? (
                        <>
                          <FiCheck className="w-3.5 h-3.5 text-white" />
                          Saved!
                        </>
                      ) : (
                        <>
                          <FiSave className="w-3.5 h-3.5 text-white" />
                          {isSaving ? "Saving…" : "Save Credentials"}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Info footer notice */}
                <div className="m-2 p-2 bg-amber-50/70 border border-amber-200/60 rounded-md flex items-center gap-2 text-amber-800 shrink-0">
                  <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <p className="text-[10px] font-medium leading-tight" style={INTER}>
                    Your credentials belong strictly to your company and cannot be accessed by other businesses.
                  </p>
                </div>
              </div>

              {/* ── RIGHT: Security & Diagnostic Panel (50% width) ── */}
              <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Right Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiShield className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">★</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        SECURITY &amp; ACCOUNT PROTECTION
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Independent merchant isolation, direct bank settlement &amp; tamper-proof verification
                      </p>
                    </div>
                  </div>

                  {/* Right Panel Scrollable Content */}
                  <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1 min-h-0">
                    
                    {/* Architecture Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-1">
                      <div className="p-3 bg-slate-50 border border-slate-200/90 rounded-xl flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-slate-800 font-bold text-[11.5px]" style={INTER}>
                          <FiLock className="w-3.5 h-3.5 text-blue-600" />
                          <span>Secret Isolation</span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-medium leading-relaxed" style={INTER}>
                          Your private Key Secret remains securely encrypted on your ERP server. It is never exposed over the internet or sent to the customer app.
                        </p>
                      </div>

                      <div className="p-3 bg-slate-50 border border-slate-200/90 rounded-xl flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-slate-800 font-bold text-[11.5px]" style={INTER}>
                          <FiDatabase className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Direct Settlement</span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-medium leading-relaxed" style={INTER}>
                          Payments settle directly from Razorpay into your jeweler bank account configured in your Razorpay dashboard. No intermediary holds your funds.
                        </p>
                      </div>
                    </div>

                    {/* How to get credentials step-by-step */}
                    <div className="border border-slate-200 rounded-xl p-3 bg-white flex flex-col gap-2">
                      <h4 className="text-[11.5px] font-bold text-slate-800 flex items-center justify-between" style={INTER}>
                        <span>How to Retrieve Your Razorpay API Credentials</span>
                        <a
                          href="https://dashboard.razorpay.com/app/keys"
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10.5px] text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 hover:underline"
                        >
                          <span>Open Razorpay Dashboard</span>
                          <FiExternalLink className="text-[10px]" />
                        </a>
                      </h4>
                      <ol className="text-[11px] text-slate-600 space-y-1.5 list-decimal pl-4 font-medium" style={INTER}>
                        <li>Log in to your <strong>Razorpay Merchant Dashboard</strong>.</li>
                        <li>Navigate to <strong>Account &amp; Settings</strong> $\to$ <strong>API Keys</strong>.</li>
                        <li>Click <strong>Generate Key</strong> to produce your Key ID and Key Secret.</li>
                        <li>Copy the Key ID and Key Secret into the fields on the left.</li>
                        <li>Click <strong>Test Connection</strong> to verify communication with the gateway.</li>
                      </ol>
                    </div>

                    {/* Security checklist */}
                    <div className="bg-blue-50/60 border border-blue-200/70 rounded-xl p-3 flex flex-col gap-1.5">
                      <span className="text-[11px] font-bold text-blue-900 flex items-center gap-1.5" style={INTER}>
                        <FiCheckSquare className="w-3.5 h-3.5 text-blue-700" />
                        Built-in Transaction Safeguards
                      </span>
                      <ul className="text-[10.5px] text-blue-800 space-y-1 font-medium pl-1" style={INTER}>
                        <li>✓ <strong>Duplicate Payment Protection:</strong> Identical payments cannot be processed twice.</li>
                        <li>✓ <strong>Cryptographic Authentication:</strong> Every payment response is validated using bank-grade digital signatures.</li>
                        <li>✓ <strong>Background Synchronization:</strong> Customer installments are recorded even if the customer's device loses connectivity.</li>
                      </ul>
                    </div>

                  </div>
                </div>

                {/* Bottom Info Banner */}
                <div className="m-3 p-2.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-center justify-between text-blue-700 text-[11.5px] shrink-0">
                  <div className="flex items-center gap-2">
                    <FiInfo className="w-4 h-4 text-blue-600 shrink-0" />
                    <span style={INTER}>
                      {lastConfigured
                        ? `Last verified configuration: ${lastConfigured}`
                        : "Ready to accept online scheme installment payments."}
                    </span>
                  </div>
                  <span className="text-[10.5px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md">
                    Company: {dbCode}
                  </span>
                </div>
              </div>
            </>
          ) : activeTab === "bank_mapping" ? (
            /* ─────────────────────────────────────────────────────────────────── */
            /* ── TAB 2: BANK ACCOUNT & VOUCHER MAPPING ───────────────────────── */
            /* ─────────────────────────────────────────────────────────────────── */
            <>
              {/* ── LEFT: Bank Account Selection Panel (50% width) ── */}
              <div className="w-[50%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiCreditCard className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        RECEIVING BANK &amp; SETTLEMENT ACCOUNT
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Select the bank account where customer digital payments will be deposited
                      </p>
                    </div>
                  </div>

                  {/* Form Scroll Area */}
                  <div className="flex flex-col gap-3.5 overflow-y-auto pr-1 flex-1 min-h-0">
                    
                    {/* Master Settlement Bank Account */}
                    <div className="flex flex-col gap-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200/90 shadow-2xs">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5" style={INTER}>
                            <FiDatabase className="text-blue-600" />
                            Primary Settlement Bank Account <span className="text-rose-500">*</span>
                          </label>
                          <span className="text-[9.5px] text-slate-500 font-medium block">
                            Master bank account where Razorpay payouts settle. All modules default to this ledger unless specifically overridden.
                          </span>
                        </div>
                        <span className="text-[9.5px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 shrink-0">
                          {cabaLedgers.length} Accounts
                        </span>
                      </div>

                      {/* SherComboBox for searchable auto-complete dropdown */}
                      <SherComboBox
                        options={cabaLedgers}
                        value={clearingLedgerName}
                        placeholder="Search & Select Primary Settlement Bank Account..."
                        onSelect={(opt) => {
                          setClearingLedgerId(String(opt.value));
                          setClearingLedgerName(opt.label);
                        }}
                        onInputChange={(text) => {
                          setClearingLedgerName(text);
                          if (!text) {
                            setClearingLedgerId("");
                          }
                        }}
                        onClear={() => {
                          setClearingLedgerId("");
                          setClearingLedgerName("");
                        }}
                        clearable={true}
                        showArrow={true}
                        inputHeight="34px"
                        inputWidth="100%"
                        dropdownMaxHeight="220px"
                        inputStyle={{
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontWeight: "500",
                          backgroundColor: "#ffffff",
                          color: "#1e293b",
                          paddingLeft: "10px",
                          ...INTER
                        }}
                      />

                      {clearingLedgerId ? (
                        <div className="flex items-center gap-1.5 text-[10.5px] text-emerald-800 font-semibold bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-200 mt-0.5">
                          <FiCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span>
                            Default Settlement: <strong>{clearingLedgerName}</strong> (Ledger Code: <strong className="font-mono">{clearingLedgerId}</strong>)
                          </span>
                        </div>
                      ) : (
                        <span className="text-[9.5px] text-slate-400 font-medium">
                          Select the bank ledger that corresponds to your Razorpay settlement account.
                        </span>
                      )}
                    </div>

                    {/* ── ON-DEMAND FORM MAPPING SECTION ── */}
                    <div className="pt-2 border-t border-slate-200 flex flex-col gap-2.5">
                      
                      {/* Section Title & Counters */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-[12px] font-bold text-slate-800 flex items-center gap-1.5" style={INTER}>
                            <FiFileText className="text-blue-600" />
                            Gateway Voucher Routes &amp; Mapping
                          </h4>
                          <p className="text-[10px] text-slate-500 font-medium" style={INTER}>
                            Add only the specific voucher forms you need for online collections. Each route receives verified transactions under its own numbering series.
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[9.5px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            {configuredForms.filter((f) => f.isEnabled).length} Active Routes
                          </span>
                          <span className="text-[9.5px] font-bold text-blue-800 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                            {configuredForms.length} Mapped
                          </span>
                        </div>
                      </div>

                      {/* ── Interactive '+ Add Voucher Route' Bar ── */}
                      <div className="bg-slate-50/90 border border-slate-200 rounded-xl p-3 flex flex-col gap-2 shadow-2xs">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5" style={INTER}>
                            <FiPlus className="w-3.5 h-3.5 text-blue-600" />
                            Add Form Route to Gateway
                          </span>
                          <span className="text-[9.5px] text-slate-400 font-medium">
                            {availableFormsToAdd.length} unmapped registered forms available
                          </span>
                        </div>

                        {/* Controls Bar: Form Selector + Bank Override + Auto-Post + Add Button */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          {/* Searchable ComboBox for Available Forms */}
                          <div className="flex-1 min-w-[220px]">
                            <SherComboBox
                              options={availableFormOptions}
                              value={
                                selectedFormToAdd
                                  ? `${selectedFormToAdd.formName} (${selectedFormToAdd.voucherType})`
                                  : ""
                              }
                              placeholder={
                                availableFormOptions.length === 0
                                  ? "All registered forms already added"
                                  : "Search & Select Form to add (e.g. Sales, Scheme)..."
                              }
                              onSelect={(opt) => {
                                if (!opt || !opt.value) {
                                  setSelectedFormToAdd(null);
                                  return;
                                }
                                const matched = allRegisteredForms.find(
                                  (f) => String(f.formId) === String(opt.value)
                                );
                                setSelectedFormToAdd(matched || null);
                              }}
                              onInputChange={(text) => {
                                if (!text) {
                                  setSelectedFormToAdd(null);
                                }
                              }}
                              onClear={() => setSelectedFormToAdd(null)}
                              clearable={true}
                              showArrow={true}
                              inputHeight="32px"
                              inputWidth="100%"
                              dropdownMaxHeight="200px"
                              inputStyle={{
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                fontSize: "11.5px",
                                fontFamily: "'Inter', system-ui, sans-serif",
                                fontWeight: "500",
                                backgroundColor: "#ffffff",
                                color: "#1e293b",
                                paddingLeft: "8px",
                                ...INTER
                              }}
                            />
                          </div>

                          {/* Specific Bank Override Dropdown */}
                          <div className="w-[180px] shrink-0">
                            <select
                              value={selectedBankToAdd.id}
                              onChange={(e) => {
                                const bId = e.target.value;
                                const matched = cabaLedgers.find((l) => String(l.value) === String(bId));
                                setSelectedBankToAdd({ id: bId, name: matched ? matched.label : "" });
                              }}
                              className="w-full h-8 px-2 text-[11px] font-medium border border-slate-300 rounded-md bg-white text-slate-700 outline-none focus:border-blue-500"
                              style={INTER}
                            >
                              <option value="">{clearingLedgerName ? `Primary Bank (${clearingLedgerName})` : "-- Select Settlement Bank --"}</option>
                              {cabaLedgers.map((l) => (
                                <option key={l.value} value={l.value}>
                                  {l.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Auto-Post Switch */}
                          <div
                            className="flex items-center gap-1.5 px-2.5 h-8 bg-white border border-slate-200 rounded-md cursor-pointer select-none shrink-0"
                            onClick={() => setSelectedAutoPostToAdd(!selectedAutoPostToAdd)}
                            title="Auto-generate official voucher immediately on successful payment"
                          >
                            <span className="text-[10.5px] font-semibold text-slate-600" style={INTER}>
                              Auto-Post
                            </span>
                            <span
                              className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${
                                selectedAutoPostToAdd ? "bg-blue-600" : "bg-slate-300"
                              }`}
                            >
                              <span
                                className={`inline-block h-2 w-2 transform rounded-full bg-white transition-transform ${
                                  selectedAutoPostToAdd ? "translate-x-3" : "translate-x-0.5"
                                }`}
                              />
                            </span>
                          </div>

                          {/* Add Button */}
                          <button
                            type="button"
                            onClick={handleAddFormMapping}
                            disabled={!selectedFormToAdd}
                            className="h-8 px-3.5 bg-[#0f2d59] hover:bg-[#1a3d70] text-white text-[11px] font-bold rounded-md shadow-2xs transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                            style={INTER}
                          >
                            <FiPlus className="w-3.5 h-3.5 text-amber-300" />
                            Add to Gateway
                          </button>
                        </div>
                      </div>

                      {/* ── Filter / Search for Configured Forms (only if > 3 items) ── */}
                      {configuredForms.length > 3 && (
                        <div className="relative flex items-center">
                          <FiSearch className="absolute left-2.5 text-slate-400 text-xs" />
                          <input
                            type="text"
                            placeholder="Filter configured forms..."
                            value={formSearchQuery}
                            onChange={(e) => setFormSearchQuery(e.target.value)}
                            className="w-full h-7 pl-8 pr-3 text-[11px] border border-slate-300 rounded-md outline-none focus:border-blue-600 bg-white placeholder-slate-400 font-medium"
                            style={INTER}
                          />
                        </div>
                      )}

                      {/* ── Configured Forms Table (Only items user explicitly chose) ── */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs max-h-[300px] overflow-y-auto">
                        {configuredForms.length === 0 ? (
                          <div className="py-10 px-4 flex flex-col items-center justify-center gap-2 text-center bg-white">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                              <FiInbox className="w-5 h-5" />
                            </div>
                            <span className="text-[12px] font-bold text-slate-700" style={INTER}>
                              No Voucher Forms Configured
                            </span>
                            <p className="text-[10.5px] text-slate-400 max-w-sm font-medium" style={INTER}>
                              Use the selector above to pick the forms you want to enable for online payments (e.g. Scheme Receipts, Cash Sales).
                            </p>
                          </div>
                        ) : (
                          <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-[#0f2d59] text-white sticky top-0 font-semibold z-10 text-[10.5px]">
                              <tr className="h-7.5">
                                <th className="py-1 px-2.5 text-center min-w-[45px] whitespace-nowrap">ID</th>
                                <th className="py-1 px-2.5 min-w-[140px] whitespace-nowrap">Form Name</th>
                                <th className="py-1 px-2.5 min-w-[110px] whitespace-nowrap">Voucher Type</th>
                                <th className="py-1 px-2 text-center min-w-[65px] whitespace-nowrap">Status</th>
                                <th className="py-1 px-2.5 min-w-[150px] whitespace-nowrap">Settlement Bank</th>
                                <th className="py-1 px-2 text-center min-w-[60px] whitespace-nowrap">Auto-Post</th>
                                <th className="py-1 px-2 text-center min-w-[40px] whitespace-nowrap">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-[11px]">
                              {filteredConfiguredForms.map((row) => {
                                const isSelected = selectedFormForPreview?.formId === row.formId;
                                const rowBg = isSelected
                                  ? "bg-blue-50/90 font-semibold"
                                  : row.isEnabled
                                  ? "bg-emerald-50/20 hover:bg-emerald-50/40"
                                  : "bg-slate-50/60 opacity-75 hover:bg-slate-100";

                                return (
                                  <tr
                                    key={row.formId}
                                    onClick={() => setSelectedFormForPreview(row)}
                                    className={`transition-colors cursor-pointer ${rowBg}`}
                                  >
                                    {/* Form ID */}
                                    <td className="py-1.5 px-2.5 text-center font-mono font-bold text-slate-700">
                                      #{row.formId}
                                    </td>

                                    {/* Form Name */}
                                    <td className="py-1.5 px-2.5 font-bold text-slate-800 whitespace-nowrap" style={INTER}>
                                      <div className="flex flex-col">
                                        <span>{row.formName}</span>
                                        {row.abbr && (
                                          <span className="text-[9px] text-slate-400 font-mono font-normal">
                                            Abbr: {row.abbr}
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Voucher Type */}
                                    <td className="py-1.5 px-2.5 whitespace-nowrap">
                                      <span className="px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                        {row.voucherType}
                                      </span>
                                    </td>

                                    {/* Online Active Switch */}
                                    <td className="py-1.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        onClick={() => handleToggleFormEnabled(row.formId)}
                                        className={`relative inline-flex h-4 w-7.5 items-center rounded-full transition-colors cursor-pointer ${
                                          row.isEnabled ? "bg-emerald-500" : "bg-slate-300"
                                        }`}
                                        title={row.isEnabled ? "Active: accepting payments" : "Paused"}
                                      >
                                        <span
                                          className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                                            row.isEnabled ? "translate-x-4" : "translate-x-0.5"
                                          }`}
                                        />
                                      </button>
                                    </td>

                                    {/* Settlement Bank Selector */}
                                    <td className="py-1.5 px-2.5" onClick={(e) => e.stopPropagation()}>
                                      <select
                                        disabled={!row.isEnabled}
                                        value={row.clearingLedgerId || ""}
                                        onChange={(e) => {
                                          const chosenId = e.target.value;
                                          const matched = cabaLedgers.find((l) => String(l.value) === String(chosenId));
                                          handleUpdateFormBank(row.formId, chosenId, matched ? matched.label : "");
                                        }}
                                        className="w-full h-7 px-1.5 text-[10.5px] font-medium border border-slate-300 rounded bg-white text-slate-800 disabled:opacity-50 disabled:bg-slate-100 outline-none"
                                        style={INTER}
                                      >
                                        <option value="">{clearingLedgerName ? `Primary Bank (${clearingLedgerName})` : "-- Select Bank --"}</option>
                                        {cabaLedgers.map((l) => (
                                          <option key={l.value} value={l.value}>
                                            {l.label}
                                          </option>
                                        ))}
                                      </select>
                                    </td>

                                    {/* Auto-Post Switch */}
                                    <td className="py-1.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        disabled={!row.isEnabled}
                                        onClick={() => handleToggleFormAutoPost(row.formId)}
                                        className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 ${
                                          row.autoPostVoucher ? "bg-blue-600" : "bg-slate-300"
                                        }`}
                                        title={row.autoPostVoucher ? "Auto-post voucher enabled" : "Manual review"}
                                      >
                                        <span
                                          className={`inline-block h-2 w-2 transform rounded-full bg-white transition-transform ${
                                            row.autoPostVoucher ? "translate-x-3" : "translate-x-0.5"
                                          }`}
                                        />
                                      </button>
                                    </td>

                                    {/* Delete / Remove Action */}
                                    <td className="py-1.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveFormMapping(row.formId)}
                                        className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors mx-auto cursor-pointer"
                                        title="Remove form route from gateway"
                                      >
                                        <FiTrash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>

                    {/* Cash Drawer Isolation Notice Box */}
                    <div className="bg-amber-50/80 border border-amber-300/80 rounded-xl p-3 flex flex-col gap-1 mt-1 shrink-0">
                      <div className="flex items-center gap-1.5 text-amber-900 font-bold text-[11.5px]" style={INTER}>
                        <FiAlertTriangle className="text-amber-600 text-sm shrink-0" />
                        <span>Cashier Drawer &amp; Bank Reconciliation Integrity</span>
                      </div>
                      <p className="text-[10.5px] text-amber-950 font-medium leading-relaxed" style={INTER}>
                        All digital transactions credit bank clearing ledgers directly. Assigning dedicated voucher forms ensures physical cash counter books remain isolated and perfectly balanced without cashier discrepancies.
                      </p>
                    </div>

                  </div>

                  {/* Form Action Buttons at bottom of left panel */}
                  <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                    <button
                      type="button"
                      onClick={fetchData}
                      className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs"
                      style={INTER}
                    >
                      Reload Accounts &amp; Forms
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving || isLoading}
                      className="h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs bg-[#b78a39] hover:bg-[#a67c32] text-white cursor-pointer disabled:opacity-50"
                      style={INTER}
                    >
                      {savedSuccess ? (
                        <>
                          <FiCheck className="w-3.5 h-3.5 text-white" />
                          Saved!
                        </>
                      ) : (
                        <>
                          <FiSave className="w-3.5 h-3.5 text-white" />
                          {isSaving ? "Saving…" : "Save Form Mappings"}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Info footer notice */}
                <div className="m-2 p-2 bg-slate-50 border border-slate-200 rounded-md flex items-center gap-2 text-slate-700 shrink-0">
                  <FiInfo className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <p className="text-[10px] font-medium leading-tight" style={INTER}>
                    Registered forms routing ensures that each business department receives verified online transactions under its own official voucher numbering series.
                  </p>
                </div>
              </div>

              {/* ── RIGHT: Accounting Voucher Preview Panel (50% width) ── */}
              <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Right Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiActivity className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">★</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        ACCOUNTING VOUCHER SPECIFICATION
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Real-time double-entry posting rules for selected registered form
                      </p>
                    </div>
                  </div>

                  {/* Voucher Preview Content */}
                  <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1 min-h-0">
                    {selectedFormForPreview ? (
                      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs bg-white">
                        <div className="bg-slate-100 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between text-[11px] font-bold text-slate-700" style={INTER}>
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-slate-800">
                              Form #{selectedFormForPreview.formId} &bull; {selectedFormForPreview.formName}
                            </span>
                            <span className="text-[9.5px] font-mono text-slate-500 uppercase px-1.5 py-0.2 bg-white rounded border border-slate-200">
                              {selectedFormForPreview.voucherType}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${
                            selectedFormForPreview.isEnabled
                              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                              : "text-slate-500 bg-slate-100 border-slate-200"
                          }`}>
                            {selectedFormForPreview.isEnabled ? "ACTIVE FOR ONLINE PAY" : "INACTIVE"}
                          </span>
                        </div>

                        {/* Balanced Double-Entry Preview */}
                        <div className="p-3 space-y-2 text-xs font-mono">
                          <div className="flex items-center justify-between p-2.5 rounded bg-emerald-50/70 border border-emerald-100">
                            <div>
                              <span className="font-bold text-emerald-800">[DEBIT]</span>{" "}
                              <span className="font-bold text-slate-800">
                                {selectedFormForPreview.clearingLedgerName || clearingLedgerName || "(Select Bank Account on left)"}
                              </span>
                              <span className="text-[10px] text-slate-500 block">
                                Settlement Bank Account &bull; Asset Ledger
                              </span>
                            </div>
                            <span className="font-bold text-emerald-800 text-sm">₹ 5,000.00</span>
                          </div>

                          <div className="flex items-center justify-between p-2.5 rounded bg-blue-50/70 border border-blue-100">
                            <div>
                              <span className="font-bold text-blue-800">[CREDIT]</span>{" "}
                              <span className="font-bold text-slate-800">
                                {selectedFormForPreview.voucherType.toUpperCase().includes("SCHEME")
                                  ? "Customer Scheme Installment Account"
                                  : selectedFormForPreview.voucherType.toUpperCase().includes("PURCHASE")
                                  ? "Supplier Purchase Ledger"
                                  : "Customer Ledger / Sales Clearance Account"}
                              </span>
                              <span className="text-[10px] text-slate-500 block">
                                {selectedFormForPreview.voucherType.toUpperCase().includes("SCHEME")
                                  ? "Scheme Member Deposit • Passbook Credit"
                                  : "Customer Account Settlement • Double-Entry Balanced"}
                              </span>
                            </div>
                            <span className="font-bold text-blue-800 text-sm">₹ 5,000.00</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 px-3 py-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-600" style={INTER}>
                          <span>
                            Numbering Series: Auto-Sequenced by <strong>{selectedFormForPreview.formName}</strong>
                          </span>
                          <span className="font-semibold text-emerald-700">
                            {selectedFormForPreview.autoPostVoucher ? "Instant Real-Time Voucher" : "Manual Post"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6 text-center text-slate-400 text-xs italic">
                        Select any registered form from the table on the left to preview its accounting specification.
                      </div>
                    )}

                    {/* Executive Active Forms Overview Box */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5" style={INTER}>
                          <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                          Currently Active Forms for Online Payment
                        </span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          {configuredForms.filter((f) => f.isEnabled).length} Active
                        </span>
                      </div>
                      <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                        {configuredForms.filter((f) => f.isEnabled).length === 0 ? (
                          <span className="text-[10.5px] text-slate-400 italic block py-2 text-center">
                            No forms currently enabled. Add and toggle voucher forms on the left.
                          </span>
                        ) : (
                          configuredForms
                            .filter((f) => f.isEnabled)
                            .map((f) => (
                              <div
                                key={f.formId}
                                onClick={() => setSelectedFormForPreview(f)}
                                className="flex items-center justify-between p-2 rounded bg-white border border-slate-200 text-xs hover:border-blue-400 cursor-pointer transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-bold text-blue-700">#{f.formId}</span>
                                  <span className="font-bold text-slate-800">{f.formName}</span>
                                  <span className="text-[9.5px] font-mono text-slate-500 uppercase px-1 bg-slate-50 rounded border border-slate-200">
                                    {f.voucherType}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-500 font-medium">
                                  Bank: <strong className="text-slate-800">{f.clearingLedgerName || clearingLedgerName || "Not Assigned"}</strong>
                                </span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                {/* Bottom Info Banner */}
                <div className="m-3 p-2.5 bg-emerald-50/80 border border-emerald-200/90 rounded-xl flex items-center gap-2 text-emerald-800 text-[11.5px] shrink-0">
                  <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span style={INTER}>
                    Official ERP vouchers are automatically created with unique transaction tokens and bank references, ensuring zero-loss audit reconciliation across all departments.
                  </span>
                </div>
              </div>
            </>
          ) : activeTab === "webhook" ? (
            /* ─────────────────────────────────────────────────────────────────── */
            /* ── TAB 3: WEBHOOK & SECURITY DIAGNOSTICS ────────────────────────── */
            /* ─────────────────────────────────────────────────────────────────── */
            <>
              {/* ── LEFT: Webhook Setup Panel (50% width) ── */}
              <div className="w-[50%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiShield className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        BACKGROUND SYNCHRONIZATION
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Guaranteed receipt creation even if customer device drops connection
                      </p>
                    </div>
                  </div>

                  {/* Form Scroll Area */}
                  <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1 min-h-0">
                    
                    {/* Webhook URL Field */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-bold text-slate-700 flex items-center justify-between" style={INTER}>
                        <span>Your Company Webhook Endpoint</span>
                        <span className="text-[9.5px] text-blue-700 font-bold bg-blue-50 px-2 py-0.2 rounded border border-blue-200">
                          Active Endpoint
                        </span>
                      </label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          readOnly
                          value={webhookUrl}
                          className="flex-1 h-[32px] px-2.5 border border-slate-200 rounded-md text-slate-700 font-mono text-[11px] bg-slate-50 outline-none select-all"
                          style={INTER}
                        />
                        <button
                          type="button"
                          onClick={() => handleCopy(webhookUrl, "url")}
                          className="px-3 h-[32px] bg-[#0f2d59] hover:bg-[#1a3d70] text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 transition cursor-pointer"
                          style={INTER}
                        >
                          {copiedUrl ? <FiCheck className="text-xs text-white" /> : <FiCopy className="text-xs text-white" />}
                          <span>{copiedUrl ? "Copied" : "Copy"}</span>
                        </button>
                      </div>
                      <span className="text-[9.5px] text-slate-400 font-medium">
                        Paste this exact address into the Webhook settings in your Razorpay dashboard.
                      </span>
                    </div>

                    {/* Webhook Events to Subscribe */}
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 flex flex-col gap-2">
                      <span className="text-[11px] font-bold text-slate-800" style={INTER}>
                        Required Event Subscriptions in Razorpay:
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono bg-white border border-emerald-300 px-2.5 py-1 rounded text-[11px] text-emerald-800 font-bold shadow-2xs">
                          payment.captured
                        </span>
                        <span className="font-mono bg-white border border-blue-300 px-2.5 py-1 rounded text-[11px] text-blue-800 font-bold shadow-2xs">
                          order.paid
                        </span>
                        <span className="font-mono bg-white border border-rose-300 px-2.5 py-1 rounded text-[11px] text-rose-800 font-bold shadow-2xs">
                          payment.failed
                        </span>
                      </div>
                    </div>

                    {/* Step-by-Step Instructions */}
                    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-1.5">
                      <h4 className="text-[11px] font-bold text-slate-800" style={INTER}>
                        Quick Setup Guide:
                      </h4>
                      <ol className="text-[10.5px] text-slate-600 space-y-1 list-decimal pl-4 font-medium" style={INTER}>
                        <li>Go to Razorpay Dashboard $\to$ <strong>Account &amp; Settings</strong> $\to$ <strong>Webhooks</strong>.</li>
                        <li>Click <strong>+ Add New Webhook</strong>.</li>
                        <li>Paste your company Webhook Endpoint copied from above.</li>
                        <li>Enter a secret of your choice and paste it into "Webhook Secret" on Tab 1.</li>
                        <li>Check the 3 events above and click <strong>Create Webhook</strong>.</li>
                      </ol>
                    </div>

                  </div>

                  {/* Form Action Buttons at bottom of left panel */}
                  <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopy(webhookUrl, "url")}
                      className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs"
                      style={INTER}
                    >
                      Copy Webhook Address
                    </button>
                    <a
                      href="https://razorpay.com/docs/webhooks/"
                      target="_blank"
                      rel="noreferrer"
                      className="h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs bg-[#0f2d59] hover:bg-[#1a3d70] text-white cursor-pointer"
                      style={INTER}
                    >
                      <span>Razorpay Documentation</span>
                      <FiExternalLink className="w-3 h-3 text-white" />
                    </a>
                  </div>
                </div>

                {/* Info footer notice */}
                <div className="m-2 p-2 bg-amber-50/70 border border-amber-200/60 rounded-md flex items-center gap-2 text-amber-800 shrink-0">
                  <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <p className="text-[10px] font-medium leading-tight" style={INTER}>
                    Webhooks ensure that even if a customer's phone disconnects right after completing payment, their receipt is posted automatically.
                  </p>
                </div>
              </div>

              {/* ── RIGHT: Webhook Fail-Safe Comparison (50% width) ── */}
              <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                  
                  {/* Right Header Banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiActivity className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">★</span>
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        RELIABILITY &amp; FALLBACK SAFEGUARDS
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        How payments are guaranteed even in poor network conditions
                      </p>
                    </div>
                  </div>

                  {/* Flow Comparison Content */}
                  <div className="flex flex-col gap-3 overflow-y-auto pr-1 flex-1 min-h-0">
                    
                    {/* Scenario 1: Normal In-App Return */}
                    <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[11.5px] font-bold text-emerald-900 flex items-center gap-1.5" style={INTER}>
                        <FiCheckCircle className="w-4 h-4 text-emerald-700" />
                        Standard Confirmation (Instant In-App)
                      </span>
                      <p className="text-[10.5px] text-emerald-800 leading-relaxed font-medium" style={INTER}>
                        Customer completes payment in UPI / Netbanking $\to$ Phone instantly returns to the customer app $\to$ The system validates payment $\to$ Customer sees instant receipt confirmation in ~1 second.
                      </p>
                    </div>

                    {/* Scenario 2: Network Drop / Phone Closed */}
                    <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[11.5px] font-bold text-blue-900 flex items-center gap-1.5" style={INTER}>
                        <FiShield className="w-4 h-4 text-blue-700" />
                        Background Fallback (Network Disconnect Protection)
                      </span>
                      <p className="text-[10.5px] text-blue-800 leading-relaxed font-medium" style={INTER}>
                        If customer's phone runs out of battery or loses signal right after UPI PIN entry, Razorpay notifies the server directly in the background. The scheme receipt is posted safely without requiring the customer's phone to be online.
                      </p>
                    </div>

                    {/* Duplicate Prevention Guard */}
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-1.5">
                      <span className="text-[11.5px] font-bold text-slate-800 flex items-center gap-1.5" style={INTER}>
                        <FiDatabase className="w-3.5 h-3.5 text-slate-700" />
                        Duplicate Payment Prevention Guard
                      </span>
                      <p className="text-[10.5px] text-slate-600 leading-relaxed font-medium" style={INTER}>
                        Even if a customer taps submit multiple times or both the mobile phone and background webhook arrive simultaneously, built-in deduplication ensures exactly <strong>one single receipt</strong> is created.
                      </p>
                    </div>

                  </div>
                </div>

                {/* Bottom Info Banner */}
                <div className="m-3 p-2.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-center gap-2 text-blue-700 text-[11.5px] shrink-0">
                  <FiInfo className="w-4 h-4 text-blue-600 shrink-0" />
                  <span style={INTER}>
                    Razorpay automatically retries webhook deliveries for up to 24 hours if temporary network disruptions occur.
                  </span>
                </div>
              </div>
            </>
          ) : (
            /* ─────────────────────────────────────────────────────────────────── */
            /* ── TAB 4: LIVE PAYMENT TRANSACTIONS & AUDIT LOG ────────────────── */
            /* ─────────────────────────────────────────────────────────────────── */
            <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden justify-between">
              <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">
                
                {/* Header Banner */}
                <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center justify-between relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                  <div className="flex items-center gap-2.5 z-10 leading-tight">
                    <FiActivity className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                    <div>
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        RECENT ONLINE SCHEME PAYMENTS
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        Official record of verified mobile app payments and auto-generated receipt vouchers
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={fetchTransactions}
                    disabled={loadingTransactions}
                    className="z-10 bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold px-3 py-1 rounded-md transition flex items-center gap-1.5 cursor-pointer"
                    style={INTER}
                  >
                    <FiRefreshCw className={`w-3 h-3 ${loadingTransactions ? "animate-spin" : ""}`} />
                    Refresh Records
                  </button>
                </div>

                {/* Table Container */}
                <div className="flex-1 min-h-0 overflow-y-auto border border-slate-200 rounded-xl bg-white shadow-2xs">
                  {loadingTransactions ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2">
                      <Spin size="default" />
                      <span className="text-xs text-slate-500 font-medium" style={INTER}>
                        Loading payment history…
                      </span>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
                      <FiActivity className="w-10 h-10 stroke-[1] mb-2 text-slate-300" />
                      <span className="text-xs font-bold text-slate-600" style={INTER}>No online payments recorded yet</span>
                      <span className="text-[11px] text-slate-400 mt-0.5" style={INTER}>
                        Payments initiated from customer mobile apps will appear here live with audit details and linked settlement accounts.
                      </span>
                    </div>
                  ) : (
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold sticky top-0" style={INTER}>
                          <th className="py-2.5 px-3">Date &amp; Time</th>
                          <th className="py-2.5 px-3">Module</th>
                          <th className="py-2.5 px-3">Order Reference</th>
                          <th className="py-2.5 px-3">Payment ID</th>
                          <th className="py-2.5 px-3">Reference / Acct</th>
                          <th className="py-2.5 px-3 text-right">Amount</th>
                          <th className="py-2.5 px-3 text-center">Status</th>
                          <th className="py-2.5 px-3 text-center">Receipt Voucher #</th>
                          <th className="py-2.5 px-3">Settlement Bank Account</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((t, idx) => (
                          <tr key={t.id || t.transactionId || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2 px-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                              {t.createdAt || t.transactionDate ? new Date(t.createdAt || t.transactionDate).toLocaleString("en-IN") : "---"}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              {(() => {
                                const mod = (t.moduleKey || t.ModuleKey || "SCHEME").toUpperCase();
                                if (mod === "SCHEME") {
                                  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">Gold Scheme</span>;
                                } else if (mod === "SALES") {
                                  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">Sales POS</span>;
                                } else if (mod === "ORDER") {
                                  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">Custom Order</span>;
                                } else if (mod === "RECEIPT") {
                                  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">Customer Dues</span>;
                                }
                                return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">{mod}</span>;
                              })()}
                            </td>
                            <td className="py-2 px-3 font-mono text-[11px] text-slate-800 font-semibold">
                              {t.razorpayOrderId}
                            </td>
                            <td className="py-2 px-3 font-mono text-[11px] text-slate-600">
                              {t.razorpayPaymentId || "---"}
                            </td>
                            <td className="py-2 px-3 font-bold text-slate-800" style={INTER}>
                              {t.schemeAccno || t.schemeAuto || t.customerRef || t.CustomerRef || "---"}
                            </td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                              ₹ {Number(t.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                  t.paymentStatus === "captured" || t.paymentStatus === "paid" || t.paymentStatus === "success"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : t.paymentStatus === "created"
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-rose-100 text-rose-800"
                                }`}
                              >
                                {t.paymentStatus}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-center">
                              {t.receiptEntryNo ? (
                                <div className="flex flex-col items-center">
                                  <span className="font-mono font-bold text-blue-700">#{t.receiptEntryNo}</span>
                                  {(t.receiptFormName || t.ReceiptFormName || t.receiptFormId || t.ReceiptFormId) && (
                                    <span className="text-[9.5px] text-slate-500 font-medium">
                                      {t.receiptFormName || t.ReceiptFormName || `Form #${t.receiptFormId || t.ReceiptFormId}`}
                                      {(t.receiptVoucherType || t.ReceiptVoucherType) && ` • ${t.receiptVoucherType || t.ReceiptVoucherType}`}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400 font-mono text-[10px]">---</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-[11px] text-slate-700">
                              {t.clearingLedgerName || t.ClearingLedgerName ? (
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-800">{t.clearingLedgerName || t.ClearingLedgerName}</span>
                                  <span className="text-[9.5px] text-slate-400 font-mono">
                                    Account #{t.clearingLedgerId || t.ClearingLedgerId}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 font-mono text-[10px]">---</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

              </div>

              {/* Bottom Info Banner */}
              <div className="m-3 p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-slate-600 text-[11.5px] shrink-0">
                <span style={INTER}>
                  Displaying last {transactions.length} online payment transactions for <strong>{dbCode}</strong>.
                </span>
                <span className="text-[10.5px] font-semibold text-emerald-700 flex items-center gap-1">
                  <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Audit Trail Active</span>
                </span>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
